<script setup lang="ts">
import { App } from '@capacitor/app';
import { useTemplateRef } from 'vue';

const userConfigStore = useUserConfigurationStore();
const siteConfigStore = useSiteConfigurationStore();
const platformStore = usePlatformStore();
const roleManagerStore = useRoleManagerStore();
const accountSettingsStore = useAccountSettingsStore();
const messageStore = useMessageStore();
const hotkeyManager = useHotkeyManager();
const tokenStore = useTokenStore();

const config = useRuntimeConfig();
const router = useRouting();
const backgroundCheckApi = useBackgroundCheckAPI();
const file = useFileAPI();

const { t } = useEkI18n();

const mfaDisplay = useTemplateRef('mfaDisplay');
const messagesModal = useTemplateRef('messagesModal');
const customersListView = useTemplateRef('customersListView');
const siteSelectDialog = useTemplateRef('siteSelect');
const fileUploads = useTemplateRef('fileUploads');
const fileUploadStore = useFileUploadStore();

const settingsIsOpen = ref(false);
const LocationsIsOpen = ref(false);

const initials = computed(() => userConfigStore.initials);
const name = computed(() => userConfigStore.config.FitterName);
const userName = computed(() => userConfigStore.config.UserName);
const siteName = computed(() => userConfigStore.trimmedSiteName);

const isOnMfaPage = computed(() => router.route.path.includes('/mfa'));
const multipleSites = computed(() => tokenStore.storedAccount.length > 1);
const version = ref('');
const build = ref('');

const showUploadsMenu = computed(() => {
  return fileUploadStore.uploadedQueue.length > 0 || fileUploadStore.queue.length > 0;
});

if (platformStore.isMobile) {
  App.getInfo().then((info) => {
    version.value = info.version;
    build.value = info.build;
  });
}

const hotKeyPopoverID = useId();

watch(() => router.route.path, () => {
  if (roleManagerStore.hasMessages === true && !userConfigStore.userConfiguration?.IsWebUser) {
    messageStore.fetchMessages(userConfigStore.fitterId);
  }
}, { immediate: true });

interface SideMenuItem{
  text: string;
  icon: string;
  shouldShow: boolean;
  clickEvent: () => void;
  badgeCount?: number;
  shouldCloseMenu: boolean;
  id?: string;
}

const sideMenuItems = computed<SideMenuItem[]>(() => [
  {
    text: t('navbar.homepage'),
    icon: 'fa-fw fa-light fa-home',
    shouldShow: userConfigStore.isLightUser === false,
    shouldCloseMenu: true,
    clickEvent: navigateToHomePage
  },
  {
    text: t('login.changeSite'),
    icon: 'fa-fw fa-light fa-globe',
    shouldShow: multipleSites.value,
    shouldCloseMenu: true,
    clickEvent: openSelectSite
  },
  {
    text: t('worksheet.customer', 2),
    icon: 'fa-fw fa-light fa-book',
    value: 'customers',
    shouldShow: userConfigStore.isLightUser === false && userConfigStore.roles.includes(Roles.FitterDebtor),
    shouldCloseMenu: true,
    clickEvent: openCustomers
  },
  {
    text: t('sidemenu.locations', 0),
    icon: 'fa-fw fa-light fa-location-dot',
    shouldShow: accountSettingsStore.accountSettings.UseInventory === true && userConfigStore.isLightUser === false,
    shouldCloseMenu: true,
    clickEvent: openLocations
  },
  {
    text: t('sidemenu.messages'),
    icon: 'fa-fw fa-light fa-envelope',
    shouldShow: roleManagerStore.hasMessages,
    shouldCloseMenu: true,
    clickEvent: openMessages,
    badgeCount: messageStore.unreadMessageCount
  },
  {
    text: t('sidemenu.callOffice'),
    icon: 'fa-fw fa-light fa-phone',
    shouldShow: platformStore.isMobileWeb || platformStore.isMobile && isStringUndefinedNullOrEmpty(siteConfigStore.config.OfficePhoneNumber) === false,
    shouldCloseMenu: true,
    clickEvent: dialPhoneNumber
  },
  {
    text: t('buttons.hotKey'),
    icon: 'fa-fw fa-light fa-keyboard',
    shouldShow: platformStore.isWeb,
    shouldCloseMenu: false,
    id: hotKeyPopoverID,
    clickEvent: () => {}
  },
  {
    text: t('sidemenu.getBackgroundCheckCertificate'),
    icon: 'fa-fw fa-light fa-file-certificate',
    shouldShow: accountSettingsStore.accountSettings.UseBackgroundCheckViaPSecure === true && userConfigStore.userConfiguration?.HasBackgroundCheckCertificate === true,
    shouldCloseMenu: true,
    clickEvent: getBackgroundCheckCertificate
  },
  {
    text: t('sidemenu.wiki'),
    icon: 'fa-fw fa-light fa-book-open',
    shouldShow: false,
    shouldCloseMenu: true,
    clickEvent: openWiki
  },
  {
    text: t('settings.settings'),
    icon: 'fa-fw fa-light fa-gear',
    shouldShow: true,
    shouldCloseMenu: true,
    clickEvent: openSettings
  },
  {
    text: t('uploads.title'),
    icon: 'fa-fw fa-light fa-upload',
    shouldShow: showUploadsMenu.value,
    shouldCloseMenu: true,
    clickEvent: openUploads
  },
  {
    text: t('sidemenu.signOut'),
    icon: 'fa-fw fa-light fa-door-closed',
    shouldShow: isStringUndefinedNullOrEmpty(initials.value) === false,
    shouldCloseMenu: true,
    clickEvent: signOut
  },
  {
    text: t('sidemenu.testPage'),
    icon: 'fa-fw fa-light fa-flask',
    shouldShow: config.public.showDevPages,
    shouldCloseMenu: true,
    clickEvent: navigateToTestPage
  }
]);

function dialPhoneNumber() {
  const phoneNumber = siteConfigStore.siteConfiguration?.OfficePhoneNumber;

  if (phoneNumber) {
    phoneApp.dialPhoneNumber(phoneNumber);
  }
}

async function getBackgroundCheckCertificate() {
  await showLoading(t('loading.downloading'));

  const response = await backgroundCheckApi.getBackgroundCheckCertificate();

  if (response && !response?.HasErrors) {
    await file.open({
      name: 'p-secure-certificate.pdf',
      data: response.Data,
      mimeType: 'application/pdf'
    });
  }

  await hideLoading();
}

function openWiki() {
  phoneApp.openWiki();
}

function openSettings() {
  settingsIsOpen.value = !settingsIsOpen.value;
}

function openUploads() {
  fileUploads.value?.open();
}

function openCustomers() {
  customersListView.value?.openCustomers();
}

function openLocations() {
  LocationsIsOpen.value = !LocationsIsOpen.value;
}

function openSelectSite() {
  siteSelectDialog.value?.openModal();
}

function navigateToTestPage() {
  router.replace('/test', true);
}

function navigateToHomePage() {
  router.replace('/home', true);
}

async function signOut() {
  //Goes in once push notification is implemented in web
  // if (siteName.value.toLowerCase() === 'ekomplet_drift2' || (siteName.value.toLowerCase() === 'ekomplet_drift2')) {
  //   await pushNotificationService.unregisterPushNotification();
  // }

  // Ionic caches pages with no way of force unloading them which results in users temporarily being able to other users state after logout.
  // We've tried to mitigate the issue in a multitude of ways but none of them have been both foolproof and perfect.
  // So instead of trying to work around it we just clear the auth tokens and reload the page at the login page.
  // It's not pretty but it always works and has the bonus of also clearing pinia state.
  useTokenStore().$reset();
  location.replace('/');
}

function openMessages() {
  messagesModal.value?.open();
}

onMounted(async () => {
  eventBus.on('openMessagesModal', () => {
    openMessages(); 
  });
  await tokenStore.getAllAccounts();
});

</script>

<template>
  <IonMenu
    ref="menu"
    content-id="main-content"
    :swipe-gesture="false"
    side="end"
    @ion-will-open="mfaDisplay?.startMfa()"
    @ion-did-close="mfaDisplay?.stopMfa()"
  >
    <IonContent>
      <IonItem
        v-if="initials !== ''"
        :detail="false"
        lines="full"
        class="nameContainer"
      >
        <IonAvatar
          slot="start"
          class="avatar-container"
        >
          <span class="avatar-text">{{ initials }}</span>
        </IonAvatar>
        <IonLabel>
          {{ name }}
          <p>{{ userName }}</p>
          <p>
            {{ siteName }}
          </p>
        </IonLabel>
      </IonItem>
      <template v-if="userConfigStore.userConfiguration?.IsWebUser">
        <IonList>
          <IonMenuToggle :auto-hide="false">
            <IonItem
              lines="none"
              :detail="false"
              button
              @click="signOut"
            >
              <IonIcon
                slot="start"
                class="fa-fw fa-light fa-door-closed"
              />
              <IonLabel> {{ t('sidemenu.signOut') }} </IonLabel>
            </IonItem>
          </IonMenuToggle>
        </IonList>
      </template>
      <template v-else>
        <IonList>
          <template
            v-for="item in sideMenuItems"
            :key="item.icon"
          >
            <IonMenuToggle
              v-if="item.shouldCloseMenu !== false"
              :auto-hide="false"
            >
              <IonItem
                v-if="item.shouldShow"
                :id="item.id"
                lines="none"
                :detail="false"
                button
                @click="item.clickEvent"
              >
                <IonBadge
                  v-if="item.badgeCount"
                  slot="end"
                  class="badge"
                >
                  {{ item.badgeCount }}
                </IonBadge>
                <IonIcon
                  slot="start"
                  :class="item.icon"
                />
                <IonLabel> {{ item.text }} </IonLabel>
              </IonItem>
            </IonMenuToggle>
            <IonItem
              v-else-if="item.shouldShow"
              :id="item.id"
              lines="none"
              :detail="false"
              button
              @click="item.clickEvent"
            >
              <IonBadge
                v-if="item.badgeCount"
                slot="end"
                class="badge"
              >
                {{ item.badgeCount }}
              </IonBadge>
              <IonIcon
                slot="start"
                :class="item.icon"
              />
              <IonLabel> {{ item.text }} </IonLabel>
            </IonItem>
          </template>
        </IonList>
      </template>

      <Modal
        v-model:open="settingsIsOpen"
        :title="t('settings.settings')"
        animation-direction="vertical"
      >
        <SettingsMenu />
      </Modal>
      <Modal
        v-model:open="LocationsIsOpen"
        :title="t('ekomplet.location', 0)"
        animation-direction="vertical"
      >
        <MaterialLocationsList />
      </Modal>
      <SiteSelectDialog ref="siteSelect" />

      <CustomersListView ref="customersListView" />
      <IonPopover
        v-if="platformStore.isWeb"
        :show-backdrop="false"
        :trigger="hotKeyPopoverID"
        side="left"
      >
        <IonContent class="ion-padding">
          <IonList>
            <template
              v-for="(group, category) in hotkeyManager.groupedHotkeys"
              :key="category"
            >
              <IonListHeader>
                <b>{{ t(`hotkeys.category.${category}`) }}</b>
              </IonListHeader>

              <IonItem
                v-for="hotkey in group"
                :key="hotkey.combo"
                lines="none"
              >
                <IonLabel class="ion-text-wrap">
                  <kbd>{{ hotkey.combo.toUpperCase() }}</kbd>
                  <IonNote class="hotkey-note">
                    {{ hotkey.description }}
                  </IonNote>
                </IonLabel>
              </IonItem>
            </template>
          </IonList>
        </IonContent>
      </IonPopover>
    </IonContent>

    <IonFooter
      v-if="platformStore.isMobile"
      class="ion-no-border"
    >
      <MFADisplay
        v-if="roleManagerStore.hasMFA && isOnMfaPage == false"
        ref="mfaDisplay"
      />
      <IonToolbar>
        <p class="text-overline version">
          {{ t('settings.version') }} {{ version }} &nbsp; {{ t('settings.build') }} {{ build }}
        </p>
      </IonToolbar>
    </IonFooter>

    <MessagesModal ref="messagesModal" />

    <UploadsFileUploads ref="fileUploads" />
  </IonMenu>
</template>

<style scoped>

.version {
  text-align: center;
  width: 100%;
}

.hotkey-note {
  display: block;
}

/*
  Push the top of the menu below the safe area so it doesn't overlay the status bar
*/
ion-menu {
  margin-top: var(--ion-safe-area-top);
}

ion-menu {
  --width: 16em;
}

.nameContainer{
  --padding-start: 7px;
  --inner-padding-start: var(--padding-start);
}

.avatar-container {
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--ion-color-primary);
  color: var(--ion-color-primary-contrast);
  height: 3em;
  width: 3em;
}

.avatar-text {
  font-size: 1.2em;
  font-weight: 700;
  letter-spacing: 0.1em;
  padding-left: 0.1em;
}

.badge{
  border-radius: 100px;
}
</style>
